import React, { useState, useEffect, useRef } from 'react';
import './App.css';

const API_BASE = '/api';

function App() {
  const [token, setToken] = useState(localStorage.getItem('token') || '');
  const [view, setView] = useState('dashboard');
  const [models, setModels] = useState([]);
  const [scripts, setScripts] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [config, setConfig] = useState({});
  const [media, setMedia] = useState({ files: [], dirs: [] });
  const [activeJob, setActiveJob] = useState(null);
  const [logText, setLogText] = useState('');

  useEffect(() => {
    if (token) {
      localStorage.setItem('token', token);
      fetchModels();
      fetchScripts();
      fetchJobs();
      fetchConfig();
      fetchMedia();
    }
  }, [token]);

  const api = async (path, opts = {}) => {
    const res = await fetch(`${API_BASE}${path}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...opts.headers
      },
      ...opts
    });
    if (res.status === 401) { setToken(''); localStorage.removeItem('token'); }
    return res.json();
  };

  const fetchModels = () => api('/models').then(r => setModels(r.models || []));
  const fetchScripts = () => api('/scripts').then(r => setScripts(r.scripts || []));
  const fetchJobs = () => api('/jobs').then(r => setJobs(r.jobs || []));
  const fetchConfig = () => api('/config/openclaw.json').then(r => setConfig(r || {}));
  const fetchMedia = (dir = '') => api(`/media/list?dir=${encodeURIComponent(dir)}`).then(r => setMedia(r || { files: [], dirs: [] }));

  const runScript = async (scriptId, payload) => {
    const r = await api(`/scripts/${scriptId}/turn`, { method: 'POST', body: JSON.stringify(payload) });
    if (r.job_id) { setActiveJob(r.job_id); setView('logs'); startLogStream(r.job_id); }
  };

  const startLogStream = (jobId) => {
    const es = new EventSource(`${API_BASE}/logs/${jobId}?token=${encodeURIComponent(token)}`);
    es.onmessage = (e) => setLogText(prev => prev + e.data + '\n');
    es.onerror = () => es.close();
  };

  const Login = () => {
    const [user, setUser] = useState('');
    const [pass, setPass] = useState('');
    const submit = async (e) => {
      e.preventDefault();
      const r = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: user, password: pass })
      });
      const data = await r.json();
      if (data.token) setToken(data.token);
    };
    return (
      <div className="login">
        <h2>OpenClaw Control</h2>
        <form onSubmit={submit}>
          <input placeholder="Username" value={user} onChange={e => setUser(e.target.value)} />
          <input type="password" placeholder="Password" value={pass} onChange={e => setPass(e.target.value)} />
          <button type="submit">Login</button>
        </form>
      </div>
    );
  };

  const Nav = () => (
    <nav className="nav">
      <button onClick={() => setView('dashboard')}>Dashboard</button>
      <button onClick={() => setView('models')}>Models</button>
      <button onClick={() => setView('scripts')}>Scripts</button>
      <button onClick={() => setView('jobs')}>Jobs</button>
      <button onClick={() => setView('logs')}>Logs</button>
      <button onClick={() => setView('media')}>Media</button>
      <button onClick={() => setView('config')}>Config</button>
      <button onClick={() => setView('ollama')}>Ollama</button>
      <button onClick={() => setView('comfy')}>ComfyUI</button>
      <button onClick={() => { setToken(''); localStorage.removeItem('token'); }}>Logout</button>
    </nav>
  );

  const Dashboard = () => (
    <div className="panel">
      <h2>Dashboard</h2>
      <div className="stats">
        <div className="stat"><div className="number">{models.length}</div><div>Models</div></div>
        <div className="stat"><div className="number">{scripts.length}</div><div>Scripts</div></div>
        <div className="stat"><div className="number">{jobs.length}</div><div>Jobs</div></div>
      </div>
    </div>
  );

  const ModelsView = () => (
    <div className="panel">
      <h2>Models</h2>
      <ul className="list">
        {models.map(m => <li key={m.id} className={m.active ? 'active' : ''}>{m.id}</li>)}
      </ul>
    </div>
  );

  const ScriptsView = () => (
    <div className="panel">
      <h2>Scripts</h2>
      {scripts.map(s => (
        <div key={s.id} className="script-card">
          <div className="title">{s.icon} {s.label}</div>
          <div className="desc">{s.description}</div>
          <button onClick={() => runScript(s.id, {})}>Run</button>
        </div>
      ))}
    </div>
  );

  const JobsView = () => (
    <div className="panel">
      <h2>Jobs</h2>
      <button onClick={fetchJobs}>Refresh</button>
      <ul className="list">
        {jobs.map(j => (
          <li key={j.id} className={j.status} onClick={() => { setActiveJob(j.id); setView('logs'); }}>
            {j.id} — {j.status}
          </li>
        ))}
      </ul>
    </div>
  );

  const LogsView = () => (
    <div className="panel">
      <h2>Logs {activeJob ? `— ${activeJob}` : ''}</h2>
      <pre className="logs">{logText}</pre>
    </div>
  );

  const MediaView = () => (
    <div className="panel">
      <h2>Media</h2>
      <button onClick={() => fetchMedia()}>Refresh</button>
      <div className="media-grid">
        {media.files.map(f => (
          <div key={f.path} className="media-item">
            <img src={`${API_BASE}/media/file?path=${encodeURIComponent(f.path)}`} alt={f.name} />
            <div className="caption">{f.name}</div>
          </div>
        ))}
      </div>
    </div>
  );

  const ConfigView = () => (
    <div className="panel">
      <h2>Config</h2>
      <pre className="code">{JSON.stringify(config, null, 2)}</pre>
    </div>
  );

  const OllamaView = () => {
    const [model, setModel] = useState('');
    const [prompt, setPrompt] = useState('');
    const [resp, setResp] = useState('');
    const submit = async () => {
      const r = await api('/ollama/generate', { method: 'POST', body: JSON.stringify({ model, prompt }) });
      setResp(r.response || JSON.stringify(r));
    };
    return (
      <div className="panel">
        <h2>Ollama Test</h2>
        <input placeholder="Model" value={model} onChange={e => setModel(e.target.value)} />
        <textarea placeholder="Prompt" value={prompt} onChange={e => setPrompt(e.target.value)} />
        <button onClick={submit}>Generate</button>
        <pre className="code">{resp}</pre>
      </div>
    );
  };

  const ComfyView = () => {
    const [prompt, setPrompt] = useState('');
    const [resp, setResp] = useState('');
    const submit = async () => {
      const r = await api('/comfy/generate', { method: 'POST', body: JSON.stringify({ prompt }) });
      setResp(r.response || JSON.stringify(r));
    };
    return (
      <div className="panel">
        <h2>ComfyUI Test</h2>
        <textarea placeholder="Prompt" value={prompt} onChange={e => setPrompt(e.target.value)} />
        <button onClick={submit}>Generate</button>
        <pre className="code">{resp}</pre>
      </div>
    );
  };

  if (!token) return <Login />;
  return (
    <div className="app">
      <Nav />
      <main>
        {view === 'dashboard' && <Dashboard />}
        {view === 'models' && <ModelsView />}
        {view === 'scripts' && <ScriptsView />}
        {view === 'jobs' && <JobsView />}
        {view === 'logs' && <LogsView />}
        {view === 'media' && <MediaView />}
        {view === 'config' && <ConfigView />}
        {view === 'ollama' && <OllamaView />}
        {view === 'comfy' && <ComfyView />}
      </main>
    </div>
  );
}

export default App;
